import React from 'react';
import Card from '../../ui/Card';
import DescriptionPanel from '../../ui/DescriptionPanel';
import { TrendingUpIcon, TrendingDownIcon } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
const LeadershipKPIsDashboard: React.FC = () => {
  // Section A - Core Financial KPIs
  const financialKPIs = [{
    title: 'Revenue',
    value: '₹9,250 Cr',
    change: '+5.2%',
    isPositive: true,
    target: '₹9,000 Cr',
    achievement: 102.8
  }, {
    title: 'EBITDA %',
    value: '18.3%',
    change: '+0.8%',
    isPositive: true,
    target: '17.5%',
    achievement: 104.6
  }, {
    title: 'PAT %',
    value: '11.2%',
    change: '+0.5%',
    isPositive: true,
    target: '10.5%',
    achievement: 106.7
  }, {
    title: 'Working Capital Days',
    value: '46',
    change: '-3 days',
    isPositive: true,
    target: '45',
    achievement: 97.8
  }, {
    title: 'Capex Utilization %',
    value: '78.5%',
    change: '+4.2%',
    isPositive: true,
    target: '80.0%',
    achievement: 98.1
  }];
  // Section B - Operational KPIs
  const operationalKPIs = [{
    title: 'OEE (Overall)',
    value: '78.5%',
    change: '+2.8%',
    isPositive: true,
    target: '85.0%'
  }, {
    title: 'Scrap Rate %',
    value: '2.1%',
    change: '-0.4%',
    isPositive: true,
    target: '1.5%'
  }, {
    title: 'Quality PPM',
    value: '285',
    change: '-42',
    isPositive: true,
    target: '200'
  }, {
    title: 'Warranty Cost',
    value: '₹185 Cr',
    change: '+2.1%',
    isPositive: false,
    target: '₹165 Cr'
  }, {
    title: 'Field Failure Rate',
    value: '1.8%',
    change: '-0.3%',
    isPositive: true,
    target: '1.2%'
  }];
  // Section C - Top 5 Customers
  const top5Customers = [{
    customer: 'Maruti Suzuki',
    revenue: '₹1,710 Cr',
    margin: '16.2%',
    yoy: '+8.5%',
    share: '18.5%'
  }, {
    customer: 'Hyundai',
    revenue: '₹1,387 Cr',
    margin: '15.8%',
    yoy: '+6.2%',
    share: '15.0%'
  }, {
    customer: 'Tata Motors',
    revenue: '₹1,295 Cr',
    margin: '17.1%',
    yoy: '+12.4%',
    share: '14.0%'
  }, {
    customer: 'Mahindra',
    revenue: '₹1,110 Cr',
    margin: '14.5%',
    yoy: '+4.8%',
    share: '12.0%'
  }, {
    customer: 'Honda',
    revenue: '₹925 Cr',
    margin: '16.8%',
    yoy: '+7.2%',
    share: '10.0%'
  }];
  // Customer/Market KPIs
  const customerMarketData = {
    oemRevenue: 68,
    aftermarketRevenue: 32,
    marketShare: '12.5%',
    newProgramWins: 8,
    rfqToSopConversion: '19%'
  };
  // Section D - Employee & People KPIs
  const peopleKPIs = [{
    title: 'Productivity per Employee',
    value: '₹42.5 L',
    change: '+6.8%',
    isPositive: true
  }, {
    title: 'Attrition %',
    value: '8.2%',
    change: '-1.5%',
    isPositive: true
  }, {
    title: 'Training Coverage %',
    value: '94.5%',
    change: '+3.2%',
    isPositive: true
  }, {
    title: 'Safety LTIFR',
    value: '0.42',
    change: '-0.08',
    isPositive: true
  }];
  // Section E - Plant Benchmarking
  const plantBenchmarkData = [{
    plant: 'Manesar',
    oee: 76.5,
    scrap: 2.1,
    ppm: 295,
    energyPerUnit: 2.8,
    auditScore: 92
  }, {
    plant: 'Chennai',
    oee: 82.0,
    scrap: 1.8,
    ppm: 245,
    energyPerUnit: 2.5,
    auditScore: 94
  }, {
    plant: 'Pune',
    oee: 75.0,
    scrap: 2.4,
    ppm: 325,
    energyPerUnit: 3.1,
    auditScore: 90
  }, {
    plant: 'Bangalore',
    oee: 79.0,
    scrap: 1.9,
    ppm: 265,
    energyPerUnit: 2.6,
    auditScore: 93
  }];
  // Section F - CEO Storylines
  const storylines = [{
    title: 'What Improved This Quarter',
    items: ['OEE increased by 2.8% to 78.5% through process optimization', 'EBITDA margin expanded by 80 bps to 18.3%', 'Working capital cycle reduced by 3 days to 46 days', 'Scrap rate decreased by 0.4% to 2.1%'],
    color: 'from-green-500 to-emerald-600'
  }, {
    title: 'What Got Worse',
    items: ['Warranty costs increased by 2.1% to ₹185 Cr', 'Pune plant OEE remains 10% below target', 'Quality PPM at 285, still above target of 200', 'Customer concentration risk at 18.5% (top customer)'],
    color: 'from-red-500 to-rose-600'
  }, {
    title: 'Where We Exceeded Targets',
    items: ['Revenue at 102.8% of target (₹9,250 Cr vs ₹9,000 Cr)', 'EBITDA margin at 104.6% of target (18.3% vs 17.5%)', 'PAT margin at 106.7% of target (11.2% vs 10.5%)', 'Chennai plant leading with 94 audit score'],
    color: 'from-blue-500 to-indigo-600'
  }, {
    title: 'Where We Lagged and Why',
    items: ['OEE at 92.4% of target due to Pune plant constraints', 'Warranty cost 12.1% above target due to LED headlamp issues', 'Quality PPM 42.5% above target - supplier quality focus needed', 'Capex utilization at 98.1% - investment timing delays'],
    color: 'from-yellow-500 to-orange-600'
  }];
  return <div data-id="element-2882">
      <DescriptionPanel title="Leadership KPIs Dashboard" description="Executive-level view of critical business metrics across financial, operational, customer, and people dimensions. Track performance against targets, benchmark plants, and understand key business drivers for strategic decision-making." insights={['Revenue at 102.8% of target with strong EBITDA margin expansion', 'Top 5 customers contributing 69.5% of revenue with healthy margins', 'Chennai plant leading performance - replication opportunity identified', 'Warranty cost escalation requires immediate attention - ₹185 Cr vs ₹165 Cr target']} data-id="element-2883" />
      {/* SECTION A - Core Financial KPIs */}
      <div className="mb-8" data-id="element-2884">
        <h2 className="text-xl font-bold text-gray-900 mb-6" data-id="element-2885">
          Core Financial KPIs
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6" data-id="element-2886">
          {financialKPIs.map((kpi, index) => <Card key={index} className="p-5 bg-gradient-to-br from-white to-blue-50" data-id="element-2887">
              <div className="text-xs font-bold text-gray-600 uppercase tracking-wide mb-2" data-id="element-2888">
                {kpi.title}
              </div>
              <div className="text-3xl font-bold text-gray-900 mb-2" data-id="element-2889">
                {kpi.value}
              </div>
              <div className="text-xs text-gray-600 mb-2" data-id="element-2890">
                Target: {kpi.target}
              </div>
              <div className="flex items-center justify-between" data-id="element-2891">
                <div className={`text-sm font-bold flex items-center ${kpi.isPositive ? 'text-green-600' : 'text-red-600'}`} data-id="element-2892">
                  {kpi.isPositive ? <TrendingUpIcon size={14} className="mr-1" data-id="element-2893" /> : <TrendingDownIcon size={14} className="mr-1" data-id="element-2894" />}
                  {kpi.change}
                </div>
                <span className={`text-xs font-bold px-2 py-1 rounded ${kpi.achievement >= 100 ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`} data-id="element-2895">
                  {kpi.achievement.toFixed(1)}%
                </span>
              </div>
            </Card>)}
        </div>
      </div>
      {/* SECTION B - Operational KPIs */}
      <div className="mb-8" data-id="element-2896">
        <h2 className="text-xl font-bold text-gray-900 mb-6" data-id="element-2897">
          Operational KPIs
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6" data-id="element-2898">
          {operationalKPIs.map((kpi, index) => <Card key={index} className="p-5 bg-gradient-to-br from-white to-purple-50" data-id="element-2899">
              <div className="text-xs font-bold text-gray-600 uppercase tracking-wide mb-2" data-id="element-2900">
                {kpi.title}
              </div>
              <div className="text-3xl font-bold text-gray-900 mb-2" data-id="element-2901">
                {kpi.value}
              </div>
              <div className="text-xs text-gray-600 mb-2" data-id="element-2902">
                Target: {kpi.target}
              </div>
              <div className={`text-sm font-bold flex items-center ${kpi.isPositive ? 'text-green-600' : 'text-red-600'}`} data-id="element-2903">
                {kpi.isPositive ? <TrendingUpIcon size={14} className="mr-1" data-id="element-2904" /> : <TrendingDownIcon size={14} className="mr-1" data-id="element-2905" />}
                {kpi.change}
              </div>
            </Card>)}
        </div>
      </div>
      {/* SECTION C - Customer/Market KPIs */}
      <div className="mb-8" data-id="element-2906">
        <h2 className="text-xl font-bold text-gray-900 mb-6" data-id="element-2907">
          Customer & Market Performance
        </h2>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-2908">
          {/* Top 5 Customers Table */}
          <Card className="p-6 lg:col-span-2" data-id="element-2909">
            <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-2910">
              Top 5 Customers
            </h3>
            <div className="overflow-x-auto" data-id="element-2911">
              <table className="w-full text-sm" data-id="element-2912">
                <thead data-id="element-2913">
                  <tr className="border-b-2 border-gray-300" data-id="element-2914">
                    <th className="text-left py-2 px-3 font-bold text-gray-700" data-id="element-2915">
                      Customer
                    </th>
                    <th className="text-right py-2 px-3 font-bold text-gray-700" data-id="element-2916">
                      Revenue
                    </th>
                    <th className="text-center py-2 px-3 font-bold text-gray-700" data-id="element-2917">
                      Margin %
                    </th>
                    <th className="text-center py-2 px-3 font-bold text-gray-700" data-id="element-2918">
                      YoY Growth
                    </th>
                    <th className="text-center py-2 px-3 font-bold text-gray-700" data-id="element-2919">
                      Share %
                    </th>
                  </tr>
                </thead>
                <tbody data-id="element-2920">
                  {top5Customers.map((customer, index) => <tr key={index} className="border-b border-gray-100" data-id="element-2921">
                      <td className="py-2 px-3 font-medium text-gray-900" data-id="element-2922">
                        {customer.customer}
                      </td>
                      <td className="py-2 px-3 text-right font-semibold text-gray-900" data-id="element-2923">
                        {customer.revenue}
                      </td>
                      <td className="py-2 px-3 text-center text-green-600 font-semibold" data-id="element-2924">
                        {customer.margin}
                      </td>
                      <td className="py-2 px-3 text-center text-blue-600 font-semibold" data-id="element-2925">
                        {customer.yoy}
                      </td>
                      <td className="py-2 px-3 text-center text-gray-700 font-semibold" data-id="element-2926">
                        {customer.share}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
            <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-2927">
              <div className="text-sm text-gray-600" data-id="element-2928">
                <span className="font-semibold" data-id="element-2929">Total Top 5:</span> ₹6,427 Cr
                (69.5% of revenue)
              </div>
              <div className="text-sm text-gray-600" data-id="element-2930">
                <span className="font-semibold" data-id="element-2931">Avg Margin:</span> 16.1%
              </div>
            </div>
          </Card>
          {/* Market KPIs */}
          <Card className="p-6" data-id="element-2932">
            <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-2933">
              Market Metrics
            </h3>
            <div className="space-y-4" data-id="element-2934">
              <div data-id="element-2935">
                <div className="text-xs text-gray-600 mb-1" data-id="element-2936">
                  OEM vs Aftermarket Revenue
                </div>
                <div className="flex items-center space-x-2" data-id="element-2937">
                  <div className="flex-1 bg-gray-100 rounded-lg overflow-hidden h-8" data-id="element-2938">
                    <div className="bg-blue-500 h-full flex items-center justify-center text-white text-xs font-bold" style={{
                    width: `${customerMarketData.oemRevenue}%`
                  }} data-id="element-2939">
                      {customerMarketData.oemRevenue}%
                    </div>
                  </div>
                  <div className="flex-1 bg-gray-100 rounded-lg overflow-hidden h-8" data-id="element-2940">
                    <div className="bg-green-500 h-full flex items-center justify-center text-white text-xs font-bold" style={{
                    width: `${customerMarketData.aftermarketRevenue}%`
                  }} data-id="element-2941">
                      {customerMarketData.aftermarketRevenue}%
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between text-xs text-gray-600 mt-1" data-id="element-2942">
                  <span data-id="element-2943">OEM</span>
                  <span data-id="element-2944">Aftermarket</span>
                </div>
              </div>
              <div className="border-t border-gray-200 pt-3" data-id="element-2945">
                <div className="flex items-center justify-between mb-2" data-id="element-2946">
                  <span className="text-xs text-gray-600" data-id="element-2947">Market Share</span>
                  <span className="text-lg font-bold text-blue-600" data-id="element-2948">
                    {customerMarketData.marketShare}
                  </span>
                </div>
                <div className="flex items-center justify-between mb-2" data-id="element-2949">
                  <span className="text-xs text-gray-600" data-id="element-2950">
                    New Program Wins
                  </span>
                  <span className="text-lg font-bold text-green-600" data-id="element-2951">
                    {customerMarketData.newProgramWins}
                  </span>
                </div>
                <div className="flex items-center justify-between" data-id="element-2952">
                  <span className="text-xs text-gray-600" data-id="element-2953">
                    RFQ to SOP Conversion
                  </span>
                  <span className="text-lg font-bold text-purple-600" data-id="element-2954">
                    {customerMarketData.rfqToSopConversion}
                  </span>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>
      {/* SECTION D - Employee & People KPIs */}
      <div className="mb-8" data-id="element-2955">
        <h2 className="text-xl font-bold text-gray-900 mb-6" data-id="element-2956">
          Employee & People KPIs
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6" data-id="element-2957">
          {peopleKPIs.map((kpi, index) => <Card key={index} className="p-5 bg-gradient-to-br from-white to-teal-50" data-id="element-2958">
              <div className="text-xs font-bold text-gray-600 uppercase tracking-wide mb-2" data-id="element-2959">
                {kpi.title}
              </div>
              <div className="text-3xl font-bold text-gray-900 mb-2" data-id="element-2960">
                {kpi.value}
              </div>
              <div className={`text-sm font-bold flex items-center ${kpi.isPositive ? 'text-green-600' : 'text-red-600'}`} data-id="element-2961">
                {kpi.isPositive ? <TrendingUpIcon size={14} className="mr-1" data-id="element-2962" /> : <TrendingDownIcon size={14} className="mr-1" data-id="element-2963" />}
                {kpi.change}
              </div>
            </Card>)}
        </div>
      </div>
      {/* SECTION E - Plant Benchmarking */}
      <Card className="p-6 mb-8" data-id="element-2964">
        <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-2965">
          Plant Benchmarking Snapshot
        </h3>
        <div className="overflow-x-auto" data-id="element-2966">
          <table className="w-full text-sm" data-id="element-2967">
            <thead data-id="element-2968">
              <tr className="border-b-2 border-gray-300" data-id="element-2969">
                <th className="text-left py-3 px-4 font-bold text-gray-700" data-id="element-2970">
                  Plant
                </th>
                <th className="text-center py-3 px-4 font-bold text-gray-700" data-id="element-2971">
                  OEE %
                </th>
                <th className="text-center py-3 px-4 font-bold text-gray-700" data-id="element-2972">
                  Scrap %
                </th>
                <th className="text-center py-3 px-4 font-bold text-gray-700" data-id="element-2973">
                  PPM
                </th>
                <th className="text-center py-3 px-4 font-bold text-gray-700" data-id="element-2974">
                  Energy/Unit
                </th>
                <th className="text-center py-3 px-4 font-bold text-gray-700" data-id="element-2975">
                  Audit Score
                </th>
              </tr>
            </thead>
            <tbody data-id="element-2976">
              {plantBenchmarkData.map((plant, index) => <tr key={index} className="border-b border-gray-100 hover:bg-gray-50" data-id="element-2977">
                  <td className="py-3 px-4 font-medium text-gray-900" data-id="element-2978">
                    {plant.plant}
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-2979">
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold ${plant.oee >= 80 ? 'bg-green-100 text-green-700' : plant.oee >= 75 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`} data-id="element-2980">
                      {plant.oee}%
                    </span>
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-2981">
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold ${plant.scrap <= 2.0 ? 'bg-green-100 text-green-700' : plant.scrap <= 2.3 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`} data-id="element-2982">
                      {plant.scrap}%
                    </span>
                  </td>
                  <td className="py-3 px-4 text-center font-semibold text-gray-700" data-id="element-2983">
                    {plant.ppm}
                  </td>
                  <td className="py-3 px-4 text-center font-semibold text-gray-700" data-id="element-2984">
                    {plant.energyPerUnit}
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-2985">
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold ${plant.auditScore >= 93 ? 'bg-green-100 text-green-700' : plant.auditScore >= 91 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`} data-id="element-2986">
                      {plant.auditScore}
                    </span>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
        <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-2987">
          <div className="text-sm text-gray-600" data-id="element-2988">
            <span className="font-semibold" data-id="element-2989">Best Performer:</span> Chennai (OEE:
            82%, Scrap: 1.8%, Audit: 94)
          </div>
          <div className="text-sm text-gray-600" data-id="element-2990">
            <span className="font-semibold" data-id="element-2991">Focus Area:</span> Pune (OEE: 75%,
            Scrap: 2.4%)
          </div>
        </div>
      </Card>
      {/* SECTION F - CEO-Ready Mini Storylines */}
      <div className="mb-8" data-id="element-2992">
        <h2 className="text-xl font-bold text-gray-900 mb-6" data-id="element-2993">
          CEO-Ready Business Storylines
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2994">
          {storylines.map((story, index) => <Card key={index} className={`p-6 bg-gradient-to-br ${story.color} text-white`} data-id="element-2995">
              <h4 className="text-lg font-bold mb-4" data-id="element-2996">{story.title}</h4>
              <ul className="space-y-2" data-id="element-2997">
                {story.items.map((item, itemIndex) => <li key={itemIndex} className="flex items-start text-sm" data-id="element-2998">
                    <span className="mr-2" data-id="element-2999">•</span>
                    <span data-id="element-3000">{item}</span>
                  </li>)}
              </ul>
            </Card>)}
        </div>
      </div>
    </div>;
};
export default LeadershipKPIsDashboard;